import { isArray } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { PortfolioTransactionTypeBlock } from '@/modules/issuer-dashboard/components/portfolios/Tabs/PortfolioTransactionsTab.tsx';
import {
  useIssuerPortfolioTransactionsInfiniteListQuery,
  useIssuerPortfolioTransactionsListQuery,
} from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import { SelectPaginate, SelectPaginateOption } from 'ui';
import { goodFormatNumber, goodFormatUSDCurrency } from 'ui/utils';
import {
  PortfolioTransactionExtended,
  PortfolioTransactionStatus,
  PortfolioTransactionType,
} from 'api';

export const mapPortfolioTransactionTypeBookToSettled: Partial<
  Record<PortfolioTransactionType, PortfolioTransactionType>
> = {
  [PortfolioTransactionType.TradeBuyBook]: PortfolioTransactionType.TradeBuySettle,
  [PortfolioTransactionType.TradeSellBook]: PortfolioTransactionType.TradeSellSettle,
  [PortfolioTransactionType.TradeBuySettle]: PortfolioTransactionType.TradeBuyBook,
  [PortfolioTransactionType.TradeSellSettle]: PortfolioTransactionType.TradeSellBook,
  [PortfolioTransactionType.IncomeDividendReinvestment]:
    PortfolioTransactionType.IncomeDividendReinvestment,
  [PortfolioTransactionType.IncomeCashInterest]: PortfolioTransactionType.IncomeCashInterest,
  [PortfolioTransactionType.IncomeDividendCash]: PortfolioTransactionType.IncomeDividendCash,
  [PortfolioTransactionType.IncomeRental]: PortfolioTransactionType.IncomeRental,
  [PortfolioTransactionType.Fee]: PortfolioTransactionType.Fee,
};

type baseProps = {
  clearable?: boolean;
  label?: string;
  formFieldTitle?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  withBorder?: boolean;
  registerId?: number;
  placeholder?: string;
  portfolioId: number;
  assetId?: number;
  transactionType?: PortfolioTransactionType;
  status?: PortfolioTransactionStatus;
};

type singleProps = {
  isMulti?: false;
  onChange: (
    options: SelectPaginateOption<PortfolioTransactionExtended> | null,
    fieldName: string,
  ) => void;
  value?: number;
};

type multiProps = {
  isMulti?: true;
  onChange: (
    options: SelectPaginateOption<PortfolioTransactionExtended>[] | null,
    fieldName: string,
  ) => void;
  value?: number[];
};

type SelectPortfolioTransactionsProps = baseProps & (singleProps | multiProps);

export const SelectPortfolioTransactions = ({
  error,
  label,
  isMulti = false,
  disabled,
  clearable,
  className,
  withBorder = false,
  value,
  formFieldTitle = '',
  onChange,
  registerId,
  placeholder,
  portfolioId,
  assetId,
  transactionType,
  status,
}: SelectPortfolioTransactionsProps) => {
  const [params] = useSearchParams();
  const register_id = +(params.get(REGISTER_ID_SEARCH_PARAM_KEY) || 0);
  const { fetchNextPage } = useIssuerPortfolioTransactionsInfiniteListQuery(
    {
      page: 1,
      page_size: 10,
      register_id: registerId || register_id,
      portfolio_id: portfolioId,
      asset_id: assetId,
      transaction_type: transactionType,
      status,
    },
    true,
  );
  const { data: dataTransactions } = useIssuerPortfolioTransactionsListQuery({
    page: 1,
    page_size: 50,
    register_id: registerId || register_id,
    portfolio_id: portfolioId,
    asset_id: assetId,
  });
//
  const allTransactions = useMemo<SelectPaginateOption<PortfolioTransactionExtended>[]>(() => {
    return (dataTransactions?.data || [])?.map(item => {
      const { asset, transaction_type, quantity, price_per_share, from_bank, status } = item;

      return {
        label: (
          <span className="flex gap-4 items-center justify-between font-[Inter] text-sm pr-3">
            <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
              <span className="text-gray-900">{asset?.name}</span>
              <span className="text-gray-700">
                <PortfolioTransactionTypeBlock
                  type={transaction_type}
                  isSettle={status === PortfolioTransactionStatus.Completed}
                />
              </span>
              <span className="text-gray-500">
                {from_bank ? '-' : '+'}
                {goodFormatUSDCurrency(price_per_share * quantity)}
              </span>
              <span className="pl-3 text-gray-500">
                {from_bank ? '+' : '-'}
                {goodFormatNumber(quantity || 0)}
              </span>
            </span>
            <span></span>
          </span>
        ),
        value: item,
        toString: () => asset?.name,
      };
    });
  }, [dataTransactions?.data]);

  const valueWithLabel = useMemo<
    | SelectPaginateOption<PortfolioTransactionExtended>
    | SelectPaginateOption<PortfolioTransactionExtended>[]
    | null
    | undefined
  >(() => {
    if (!value) return null;

    const options = isArray(value)
      ? allTransactions.filter(option => value?.includes(option.value.id))
      : allTransactions.find(option => option.value.id === value);

    return options || null;
  }, [allTransactions, value]);

  const handleAsyncLoadFormOptions = useCallback(
    async (page: number) => {
      const dataTransactions = await fetchNextPage({ pageParam: page });

      const banks = dataTransactions?.data?.pages?.at(-1)?.data || [];
      return banks.map(item => {
        const { asset, transaction_type, quantity, price_per_share, from_bank, status } = item;

        return {
          label: (
            <span className="flex gap-4 items-center justify-between font-[Inter] text-sm pr-3">
              <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
                <span className="text-gray-900">{asset?.name}</span>
                <span className="text-gray-700">
                  <PortfolioTransactionTypeBlock
                    type={transaction_type}
                    isSettle={status === PortfolioTransactionStatus.Completed}
                  />
                </span>
                <span className="text-gray-500">
                  {from_bank ? '-' : '+'}
                  {goodFormatUSDCurrency(price_per_share * quantity)}
                </span>
                <span className="pl-3 text-gray-500">
                  {from_bank ? '+' : '-'}
                  {goodFormatNumber(quantity || 0)}
                </span>
              </span>
              <span></span>
            </span>
          ),
          value: item,
          toString: () => asset?.name,
        };
      });
    },
    [fetchNextPage],
  );

  return (
    <SelectPaginate
      isClearable={clearable}
      disabled={disabled}
      isMulti={isMulti}
      label={label}
      className={className}
      onAsync={handleAsyncLoadFormOptions}
      onChange={options => onChange(options as any, formFieldTitle)}
      value={valueWithLabel}
      error={error}
      withBorder={withBorder}
      placeholder={placeholder}
    />
  );
};
